import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  useCustomToast,
  VStack,
} from '@elkaso-app/web-design';
import { useCreateCrmSupplierContactQuotaApi } from 'apis/crm-supplier-contact-quotas';
import { ISupplierSearchData } from 'apis/supplier/use-search-suppliers-api';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { SupplierAsyncSelect } from '../supplier-async-select';

interface CreateQuotaModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  quota: number;
  remaining: number;
}

const defaultValues: FormData = {
  quota: 0,
  remaining: 0,
};

export const CreateQuotaModal: React.FC<CreateQuotaModalProps> = ({ isOpen, onClose }) => {
  const toast = useCustomToast();
  const [selectedSupplier, setSelectedSupplier] = useState<ISupplierSearchData | null>(null);

  // Form setup
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    defaultValues,
  });

  const quotaValue = watch('quota');

  // Create mutation
  const { mutate: createQuota, isLoading: isCreating } = useCreateCrmSupplierContactQuotaApi({
    onSuccess: () => {
      toast({
        title: 'Quota Created Successfully!',
        description: 'New supplier contact quota has been created.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      onCloseModal();
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to Create Quota',
        description: error?.response?.data?.message || 'An unexpected error occurred. Please try again.',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    },
  });

  // Close modal and reset form
  const onCloseModal = useCallback(() => {
    reset(defaultValues);
    setSelectedSupplier(null);
    onClose();
  }, [reset, onClose]);

  // Auto-set remaining quota to match total quota if not manually changed
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'quota' && value.quota !== undefined) {
        // Only auto-set remaining if it's currently 0 or matches previous quota
        const currentRemaining = value.remaining || 0;
        if (currentRemaining === 0 || currentRemaining === quotaValue) {
          reset({ ...value, remaining: value.quota });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, reset, quotaValue]);

  // Form submission
  const onSubmit = useCallback(
    (data: FormData) => {
      if (!selectedSupplier) {
        toast({
          title: 'Supplier Required',
          description: 'Please select a supplier before creating the quota.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (data.remaining > data.quota) {
        toast({
          title: 'Invalid Remaining Quota',
          description: 'Remaining quota cannot exceed total quota.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      createQuota({
        supplierId: selectedSupplier.id,
        quota: data.quota,
        remaining: data.remaining,
      });
    },
    [createQuota, selectedSupplier, toast]
  );

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size='lg' closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Supplier Contact Quota</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align='stretch'>
            {/* Supplier Selection */}
            <SupplierAsyncSelect
              value={selectedSupplier}
              onChange={setSelectedSupplier}
              isRequired={true}
              label='Supplier'
              placeholder='Search suppliers by name...'
              error={!selectedSupplier ? 'Supplier is required' : undefined}
            />

            <FormControl isInvalid={!!errors.quota} isRequired>
              <FormLabel>Total Quota</FormLabel>
              <NumberInput min={0}>
                <NumberInputField
                  {...register('quota', {
                    required: 'Total quota is required',
                    min: { value: 0, message: 'Quota must be non-negative' },
                    valueAsNumber: true,
                  })}
                  placeholder='Enter total quota'
                />
              </NumberInput>
              <FormErrorMessage>{errors.quota?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.remaining} isRequired>
              <FormLabel>Remaining Quota</FormLabel>
              <NumberInput min={0}>
                <NumberInputField
                  {...register('remaining', {
                    required: 'Remaining quota is required',
                    min: { value: 0, message: 'Remaining quota must be non-negative' },
                    valueAsNumber: true,
                  })}
                  placeholder='Enter remaining quota'
                />
              </NumberInput>
              <FormErrorMessage>{errors.remaining?.message}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button variant='outline' onClick={onCloseModal} isDisabled={isCreating}>
              Cancel
            </Button>
            <Button
              colorScheme='blue'
              onClick={handleSubmit(onSubmit)}
              isLoading={isCreating}
              loadingText='Creating...'>
              Create Quota
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
